/**
 * Consent module — single source of truth for cookie/GDPR consent state.
 *
 * State: "granted" | "denied" | null (undecided)
 * Persisted in localStorage under "btk_consent_v1" with a 365-day TTL.
 *
 * Wires into:
 *  - Google Consent Mode v2 (pushes default + update signals to GTM dataLayer)
 *  - PostHog (opts in/out of capturing + session recording)
 *
 * Public API:
 *   getConsent(): "granted" | "denied" | null
 *   setConsent(value): persist + propagate to all trackers
 *   onConsentChange(cb): subscribe to consent changes (for React state)
 *   pushDefaultConsent(): fire the GA4 Consent Mode default signal early (call once on app boot)
 */

const KEY = "btk_consent_v1";
const TTL_MS = 365 * 24 * 60 * 60 * 1000; // 1 year

let _listeners = [];

const safeWindow = () => (typeof window !== "undefined" ? window : null);

export const getConsent = () => {
  const w = safeWindow();
  if (!w) return null;
  try {
    const raw = w.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.value || !parsed.ts) return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      w.localStorage.removeItem(KEY);
      return null;
    }
    return parsed.value;
  } catch (_) {
    return null;
  }
};

const ensureDataLayer = () => {
  const w = safeWindow();
  if (!w) return null;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
};

/** Push the GA4 Consent Mode v2 DEFAULT (denied) — call once before GTM-loaded tags fire. */
export const pushDefaultConsent = () => {
  const dl = ensureDataLayer();
  if (!dl) return;
  // gtag stub for GTM-resolved tags that listen via gtag()
  const w = safeWindow();
  if (w && typeof w.gtag !== "function") {
    w.gtag = function () { w.dataLayer.push(arguments); };
  }
  // Default = denied (EU-friendly, defensive). update() flips on Accept.
  dl.push(["consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  }]);
};

/** Push GA4 Consent Mode v2 UPDATE based on the user's choice. */
const pushConsentUpdate = (value) => {
  const dl = ensureDataLayer();
  if (!dl) return;
  const granted = value === "granted";
  dl.push(["consent", "update", {
    ad_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
    analytics_storage: granted ? "granted" : "denied",
  }]);
  // Also push a custom event so users can build GTM triggers on consent decisions.
  dl.push({ event: granted ? "consent_granted" : "consent_denied" });
};

/** Wire PostHog (already loaded via index.html). */
const propagatePostHog = (value) => {
  const w = safeWindow();
  if (!w || !w.posthog) return;
  try {
    if (value === "granted") {
      if (typeof w.posthog.opt_in_capturing === "function") w.posthog.opt_in_capturing();
      if (typeof w.posthog.startSessionRecording === "function") w.posthog.startSessionRecording();
    } else {
      if (typeof w.posthog.stopSessionRecording === "function") w.posthog.stopSessionRecording();
      if (typeof w.posthog.opt_out_capturing === "function") w.posthog.opt_out_capturing();
    }
  } catch (_) { /* noop */ }
};

export const setConsent = (value) => {
  if (value !== "granted" && value !== "denied") return;
  const w = safeWindow();
  if (w) {
    try { w.localStorage.setItem(KEY, JSON.stringify({ value, ts: Date.now() })); } catch (_) {}
  }
  pushConsentUpdate(value);
  propagatePostHog(value);
  _listeners.forEach((cb) => { try { cb(value); } catch (_) {} });
};

export const onConsentChange = (cb) => {
  _listeners.push(cb);
  return () => { _listeners = _listeners.filter((x) => x !== cb); };
};

/** Re-apply current consent (e.g. after PostHog finishes loading). Idempotent. */
export const replayConsent = () => {
  const v = getConsent();
  if (v) {
    pushConsentUpdate(v);
    propagatePostHog(v);
  }
};
